import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import CustomerService from "../../services/CustomerServices";
import MasterLocationService from "../../services/MasterLocationService";
import Breadcrumb from "../../components/Breadcrumb";
import StorageService from "../../services/StorageService";
import MessageDialog from "../../components/MessageDialog";

export default function AddCustomer() {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [msgType, setMsgType] = useState("success");
  const currentYear = new Date().getFullYear();
  const [editItem, setEditItem] = useState(null);
  const navigate = useNavigate();
  const { id } = useParams();
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [editLoading, setEditLoading] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    mode: "onChange",
  });

  const country = watch("countryId");
  const state = watch("state");

  const showMessage = (text, type = "success") => {
    setMessage(text);
    setMsgType(type);
  };

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const data = await MasterLocationService.getAllCountries();
        setCountries(data || []);
      } catch (err) {
        console.error("Error fetching countries:", err);
        setCountries([]);
      }
    };
    fetchCountries();
  }, []);

  useEffect(() => {
    if (country) {
      const fetchStates = async () => {
        try {
          const data = await MasterLocationService.getStatesByCountry(country);
          setStates(data || []);
          if (!editMode) {
            setValue("state", "");
            setCities([]);
            setValue("city", "");
          }
        } catch (err) {
          console.error("Error fetching states:", err);
          setStates([]);
          setCities([]);
          setValue("state", "");
          setValue("city", "");
        }
      };
      fetchStates();
    } else {
      setStates([]);
      setCities([]);
      setValue("state", "");
      setValue("city", "");
    }
  }, [country, setValue, editMode]);

  useEffect(() => {
    if (state) {
      const fetchCities = async () => {
        try {
          const data = await MasterLocationService.getCitiesByState(state);
          setCities(data || []);
          if (!editMode) setValue("city", "");
        } catch (err) {
          console.error("Error fetching cities:", err);
          setCities([]);
          if (!editMode) setValue("city", "");
        }
      };
      fetchCities();
    } else {
      setCities([]);
      if (!editMode) setValue("city", "");
    }
  }, [state, setValue, editMode]);

  useEffect(() => {
    const loadCustomer = async () => {
      if (!id) return;

      setEditMode(true);

      try {
        setEditLoading(true);
        const res = await CustomerService.getById(id);
        const customer = res.data;
        setEditItem(customer);

        const countryList = await MasterLocationService.getAllCountries();
        setCountries(countryList || []);
        if (customer.countryId !== undefined && customer.countryId !== null) {
          setValue("countryId", String(customer.countryId));
        }

        const stateList = await MasterLocationService.getStatesByCountry(
          customer.countryId || ""
        );
        setStates(stateList || []);

        let selectedStateId = null;
        if (customer.state) {
          const foundState = (stateList || []).find(
            (s) =>
              String(s.stateName).trim().toLowerCase() ===
              String(customer.state).trim().toLowerCase()
          );
          if (foundState) {
            selectedStateId = foundState.id;
            setValue("state", String(foundState.id));
          }
        }

        const cityStateId = selectedStateId || customer.stateId || "";
        const cityList = cityStateId
          ? await MasterLocationService.getCitiesByState(cityStateId)
          : [];
        setCities(cityList || []);

        if (customer.city && cityList && cityList.length) {
          const foundCity = cityList.find(
            (c) =>
              String(c.cityName).trim().toLowerCase() ===
              String(customer.city).trim().toLowerCase()
          );
          if (foundCity) {
            setValue("city", String(foundCity.id));
          }
        }

        Object.keys(customer).forEach((key) => {
          if (!["countryId", "state", "stateId", "city", "cityId"].includes(key)) {
            const val = customer[key];
            if (val !== undefined && val !== null) {
              if (!["countryId", "state", "city"].includes(key)) {
                setValue(key, val);
              }
            }
          }
        });
      } catch (error) {
        console.error("Modify Load Error:", error);
      }
      setEditLoading(false);
    };

    loadCustomer();
  }, [id, setValue, reset]);

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      let res;

      if (editMode && editItem?.id) {
        data.updatedBy = StorageService.loginData().employeeId;
        res = await CustomerService.update(editItem.id, data);
      } else {
        data.companyId = StorageService.activeCompany().companyId;
        data.createdBy = StorageService.loginData().employeeId;
        res = await CustomerService.create({ ...data, status: "Active" });
      }

      if (res?.status) {
        showMessage(
          editMode
            ? "Customer updated successfully."
            : "Customer added successfully.",
          "success"
        );
        setTimeout(() => {
          localStorage.removeItem("editItem");
          navigate("/cpnl/customers/view");
        }, 1000);
      } else {
        showMessage(res?.message || "Failed to save Customer.", "error");
      }
    } catch (error) {
      console.error("Error saving customer:", error);
      const msg =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        "Error saving customer";
      showMessage(msg, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    localStorage.removeItem("editItem");
    reset();
    navigate("/cpnl/customers/view");
  };

  return (
    <>
      <Breadcrumb
        pageTitle={editMode ? "Modify Customer" : "Add Customer"}
        secondTitle="Masters"
      ></Breadcrumb>
      <div className="row">
        <div className="card py-2 bg-white shadow-sm">
          <MessageDialog
            message={message}
            type={msgType}
            setMessage={setMessage}
            setMsgType={setMsgType}
          />

          {editLoading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary"></div>
              <div className="mt-2 text-muted">Loading customer data...</div>
            </div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} noValidate>
              <div className="row">
                <div className="mb-3 col-md-6">
                  <label className="form-label">
                    Customer Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Customer Name"
                    {...register("customerName", {
                      required: "Customer Name is required",
                      minLength: { value: 3, message: "At least 3 characters" },
                    })}
                  />
                  {errors.customerName && (
                    <small className="text-danger">
                      {errors.customerName.message}
                    </small>
                  )}
                </div>

                <div className="mb-3 col-md-6">
                  <label className="form-label">
                    Customer Code <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Customer Code"
                    {...register("customerCode", {
                      required: "Customer Code is required",
                    })}
                    onInput={(e) => {
                      e.target.value = e.target.value.toUpperCase();
                    }}
                  />
                  {errors.customerCode && (
                    <small className="text-danger">
                      {errors.customerCode.message}
                    </small>
                  )}
                </div>
              </div>

              <div className="row">
                <div className="mb-3 col-md-6">
                  <label className="form-label">Contact First Name</label>
                  <input
                    type="text"
                    placeholder="First Name"
                    className="form-control"
                    {...register("firstName")}
                  />
                </div>

                <div className="mb-3 col-md-6">
                  <label className="form-label">Contact Last Name</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Last Name"
                    {...register("lastName")}
                  />
                </div>
              </div>

              <div className="row">
                <div className="mb-3 col-md-6">
                  <label className="form-label">Email ID</label>
                  <input
                    type="email"
                    className="form-control"
                    placeholder="Email ID"
                    {...register("emailId", {
                      pattern: {
                        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                        message: "Enter a valid email address",
                      },
                    })}
                  />
                  {errors.emailId && (
                    <small className="text-danger">
                      {errors.emailId.message}
                    </small>
                  )}
                </div>

                <div className="mb-3 col-md-6">
                  <label className="form-label">Mobile No</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="10-digit Mobile No"
                    {...register("mobileNo", {
                      pattern: {
                        value: /^[0-9]{10}$/,
                        message: "Enter a valid 10-digit mobile number",
                      },
                    })}
                  />
                  {errors.mobileNo && (
                    <small className="text-danger">
                      {errors.mobileNo.message}
                    </small>
                  )}
                </div>
              </div>

              <div className="row">
                <div className="mb-3 col-md-6">
                  <label className="form-label">Phone</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Phone"
                    {...register("phone")}
                  />
                </div>

                <div className="mb-3 col-md-6">
                  <label className="form-label">Fax</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Fax"
                    {...register("fax")}
                  />
                </div>
              </div>

              <div className="row">
                <div className="mb-3 col-md-6">
                  <label className="form-label">Address</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Address"
                    {...register("address")}
                  />
                </div>

                <div className="mb-3 col-md-6">
                  <label className="form-label">Landmark</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Landmark"
                    {...register("landMark")}
                  />
                </div>
              </div>

              <div className="row">
                <div className="mb-3 col-md-6">
                  <label className="form-label">Website</label>
                  <input
                    type="url"
                    className="form-control"
                    placeholder="Website"
                    {...register("website")}
                  />
                </div>

                <div className="mb-3 col-md-6">
                  <label className="form-label">Logo Small URL</label>
                  <input
                    type="url"
                    className="form-control"
                    placeholder="Logo Small URL"
                    {...register("logoSmallURL")}
                  />
                </div>
              </div>

              <div className="row">
                <div className="mb-3 col-md-6">
                  <label className="form-label">Logo Big URL</label>
                  <input
                    type="url"
                    className="form-control"
                    placeholder="Logo Big URL"
                    {...register("logoBigURL")}
                  />
                </div>

                <div className="mb-3 col-md-6">
                  <label className="form-label">
                    Country <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select"
                    {...register("countryId", {
                      required: "Country is required",
                    })}
                  >
                    <option value="">Select Country</option>
                    {countries.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.countryName}
                      </option>
                    ))}
                  </select>
                  {errors.countryId && (
                    <small className="text-danger">
                      {errors.countryId.message}
                    </small>
                  )}
                </div>
              </div>

              <div className="row">
                <div className="mb-3 col-md-6">
                  <label className="form-label">
                    State <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select"
                    {...register("state", { required: "State is required" })}
                  >
                    <option value="">Select State</option>
                    {states.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.stateName}
                      </option>
                    ))}
                  </select>
                  {errors.state && (
                    <small className="text-danger">{errors.state.message}</small>
                  )}
                </div>

                <div className="mb-3 col-md-6">
                  <label className="form-label">
                    District <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select"
                    {...register("city", { required: "District is required" })}
                  >
                    <option value="">Select District</option>
                    {cities.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.cityName}
                      </option>
                    ))}
                  </select>
                  {errors.city && (
                    <small className="text-danger">{errors.city.message}</small>
                  )}
                </div>
              </div>

              <div className="row">
                <div className="mb-3 col-md-6">
                  <label className="form-label">Zip</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Zip"
                    {...register("zip")}
                  />
                </div>

                <div className="mb-3 col-md-6">
                  <label className="form-label">Establishment Year</label>
                  <input
                    type="number"
                    className="form-control"
                    placeholder="YYYY"
                    {...register("establishmentYear", {
                      valueAsNumber: true,
                      validate: (v) => {
                        if (!v) return true;
                        if (v < 1900 || v > currentYear)
                          return `Enter year between 1900 and ${currentYear}`;
                        return true;
                      },
                    })}
                  />
                  {errors.establishmentYear && (
                    <small className="text-danger">
                      {errors.establishmentYear.message}
                    </small>
                  )}
                </div>
              </div>

              <div className="row">
                <div className="mb-3 col-md-6">
                  <label className="form-label">PAN</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="PAN"
                    {...register("PAN", {
                      pattern: {
                        value: /^[A-Z]{5}[0-9]{4}[A-Z]$/,
                        message: "PAN must be 10 characters (e.g. AAAAA9999A)",
                      },
                    })}
                    onInput={(e) => {
                      e.target.value = e.target.value.toUpperCase();
                    }}
                  />
                  {errors.PAN && (
                    <small className="text-danger">{errors.PAN.message}</small>
                  )}
                </div>

                <div className="mb-3 col-md-6">
                  <label className="form-label">TAN</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="TAN"
                    {...register("TAN", {
                      pattern: {
                        value: /^[A-Z]{4}[0-9]{5}[A-Z]$/,
                        message: "TAN must be 10 characters (e.g. AAAA99999A)",
                      },
                    })}
                    onInput={(e) => {
                      e.target.value = e.target.value.toUpperCase();
                    }}
                  />
                  {errors.TAN && (
                    <small className="text-danger">{errors.TAN.message}</small>
                  )}
                </div>
              </div>

              <div className="row">
                <div className="mb-3 col-md-6">
                  <label className="form-label">GSTN</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="GSTN"
                    {...register("GSTN", {
                      pattern: {
                        value: /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/,
                        message: "GSTN must be 15 characters",
                      },
                    })}
                    onInput={(e) => {
                      e.target.value = e.target.value.toUpperCase();
                    }}
                  />
                  {errors.GSTN && (
                    <small className="text-danger">{errors.GSTN.message}</small>
                  )}
                </div>

                <div className="mb-3 col-md-6">
                  <label className="form-label">CIN</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="CIN"
                    {...register("CIN", {
                      minLength: {
                        value: 21,
                        message: "CIN must be 21 characters",
                      },
                      maxLength: {
                        value: 21,
                        message: "CIN must be 21 characters",
                      },
                    })}
                  />
                  {errors.CIN && (
                    <small className="text-danger">{errors.CIN.message}</small>
                  )}
                </div>
              </div>

              <div className="row">
                <div className="mb-3 col-md-6">
                  <label className="form-label">Trademark No</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Trademark No"
                    {...register("tradeMarkNo")}
                  />
                </div>
              </div>

              <div className="mt-3">
                <button
                  type="submit"
                  className="btn btn-primary me-2"
                  disabled={loading}
                >
                  <i className="ti ti-circle-check me-1"></i>
                  {loading
                    ? editItem
                      ? "Updating..."
                      : "Saving..."
                    : editItem
                      ? "Update"
                      : "Save"}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCancel}
                >
                  <i className="ti ti-cancel me-1"></i>
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </>
  );
}
